/**
 * Notification Item
 *
 * @module NotificationItem
 *
 * Single notification row. Delegates rendering to a registered
 * renderer when available, otherwise falls back to the title.
 */

import React from 'react';
import type { NotificationData } from '../NotificationRegistry';
import { getNotificationRenderer } from '../NotificationRegistry';
import { useNotifications } from '../hooks/useNotifications';
import { X } from '@phosphor-icons/react';

interface NotificationItemProps {
  notification: NotificationData;
}

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function NotificationItem({ notification }: NotificationItemProps) {
  const { dismiss, markRead } = useNotifications();
  const Renderer = getNotificationRenderer(notification.type);

  return (
    <div
      className={`group relative flex items-start gap-3 border-b border-neutral-100 px-4 py-3 last:border-0 dark:border-neutral-700 ${
        !notification.read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
      }`}
    >
      {!notification.read && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            markRead(notification.id);
          }}
          className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-500 hover:bg-blue-400 transition-colors cursor-pointer"
          aria-label="Mark as read"
        />
      )}
      <div className="min-w-0 flex-1">
        {Renderer ? (
          <Renderer notification={notification} />
        ) : (
          <p className="text-sm text-neutral-900 dark:text-neutral-100">
            {notification.title}
          </p>
        )}
        <p className="mt-1 text-xs text-neutral-500">{timeAgo(notification.createdAt)}</p>
      </div>
      <button
        onClick={() => dismiss(notification.id)}
        className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
        aria-label="Dismiss"
      >
        <X size={14} className="text-neutral-400 hover:text-neutral-600" />
      </button>
    </div>
  );
}
